import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Skeleton,
  Button,
  Card,
  CardContent,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@org/ui';
import {
  Search,
  FileText,
  User,
  Users,
  Heart,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { extractPlainText } from '@org/utils-client';
import type { RichTextDoc } from '@org/model';

type SearchParams = {
  q?: string;
  tab?: 'posts' | 'users' | 'spaces';
};

export const Route = createFileRoute('/search')({
  component: SearchPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    q: search.q as string | undefined,
    tab: (search.tab as SearchParams['tab']) || 'posts',
  }),
});

function SearchPage() {
  const { t } = useTranslation('community');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [query, setQuery] = useState(search.q || '');
  const [page, setPage] = useState(0);
  const limit = 20;

  const activeTab = search.tab || 'posts';

  const { data: postsData, isLoading: loadingPosts } =
    communityTrpc.search.posts.useQuery(
      { query: search.q || '', limit, offset: page * limit },
      { enabled: !!search.q && activeTab === 'posts' }
    );

  const { data: usersData, isLoading: loadingUsers } =
    communityTrpc.search.users.useQuery(
      { query: search.q || '', limit, offset: page * limit },
      { enabled: !!search.q && activeTab === 'users' }
    );

  const { data: spacesData, isLoading: loadingSpaces } =
    communityTrpc.search.spaces.useQuery(
      { query: search.q || '', limit, offset: page * limit },
      { enabled: !!search.q && activeTab === 'spaces' }
    );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate({ search: { q: query.trim(), tab: activeTab } });
      setPage(0);
    }
  };

  const handleTabChange = (tab: string) => {
    navigate({ search: { q: search.q, tab: tab as SearchParams['tab'] } });
    setPage(0);
  };

  const totalPages =
    activeTab === 'posts'
      ? Math.ceil((postsData?.total ?? 0) / limit)
      : activeTab === 'users'
      ? Math.ceil((usersData?.total ?? 0) / limit)
      : Math.ceil((spacesData?.total ?? 0) / limit);

  const total =
    activeTab === 'posts'
      ? postsData?.total ?? 0
      : activeTab === 'users'
      ? usersData?.total ?? 0
      : spacesData?.total ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('search.title')}</h1>
        <p className="text-muted-foreground">{t('search.subtitle')}</p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="flex gap-2" role="search">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            placeholder={t('search.placeholder')}
            aria-label={t('search.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button type="submit">{t('search.searchButton')}</Button>
      </form>

      {search.q && (
        <>
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="posts" className="flex items-center gap-2">
                <FileText className="h-4 w-4" aria-hidden="true" />
                {t('search.tabPosts')}
                {activeTab === 'posts' && postsData && (
                  <span className="text-xs text-muted-foreground">
                    ({postsData.total})
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-2">
                <User className="h-4 w-4" aria-hidden="true" />
                {t('search.tabPeople')}
                {activeTab === 'users' && usersData && (
                  <span className="text-xs text-muted-foreground">
                    ({usersData.total})
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="spaces" className="flex items-center gap-2">
                <Users className="h-4 w-4" aria-hidden="true" />
                {t('spaces.title')}
                {activeTab === 'spaces' && spacesData && (
                  <span className="text-xs text-muted-foreground">
                    ({spacesData.total})
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Posts Results */}
            <TabsContent value="posts" className="mt-4">
              {loadingPosts ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full" />
                  ))}
                </div>
              ) : postsData?.posts.length === 0 ? (
                <EmptyState query={search.q} messageKey="search.noPostsFound" />
              ) : (
                <div className="space-y-4">
                  {postsData?.posts.map((post) => (
                    <Link
                      key={post.postId}
                      to="/posts/$postId"
                      params={{ postId: post.postId }}
                    >
                      <Card className="transition-shadow hover:shadow-md">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium">{post.title}</p>
                              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                                {extractPlainText(post.content as RichTextDoc).slice(0, 200)}
                              </p>
                              <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Heart className="h-4 w-4" aria-hidden="true" />
                                  {post.likeCount}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="h-4 w-4" aria-hidden="true" />
                                  {post.commentCount}
                                </span>
                                <span>
                                  {formatDistanceToNow(new Date(post.createdAt), {
                                    addSuffix: true,
                                  })}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                <AvatarImage
                                  src={post.author?.profileImageUrl ?? undefined}
                                />
                                <AvatarFallback>
                                  {(
                                    post.author?.displayName ||
                                    post.author?.firstName ||
                                    'U'
                                  )[0]}
                                </AvatarFallback>
                              </Avatar>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Users Results */}
            <TabsContent value="users" className="mt-4">
              {loadingUsers ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : usersData?.users.length === 0 ? (
                <EmptyState query={search.q} messageKey="search.noUsersFound" />
              ) : (
                <div className="space-y-3">
                  {usersData?.users.map((user) => (
                    <Link
                      key={user.userId}
                      to="/profile/$userId"
                      params={{ userId: user.userId }}
                    >
                      <Card className="transition-shadow hover:shadow-md">
                        <CardContent className="flex items-center gap-4 p-4">
                          <Avatar className="h-12 w-12">
                            <AvatarImage
                              src={user.profileImageUrl ?? undefined}
                            />
                            <AvatarFallback>
                              {(user.displayName || user.firstName || 'U')[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium">
                              {user.displayName ||
                                [user.firstName, user.lastName]
                                  .filter(Boolean)
                                  .join(' ')}
                            </p>
                            {user.bio && (
                              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                                {user.bio}
                              </p>
                            )}
                            <p className="mt-1 text-sm text-muted-foreground">
                              {user.points} {t('leaderboard.points')} · {t('leaderboard.level')} {user.communityLevel}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Spaces Results */}
            <TabsContent value="spaces" className="mt-4">
              {loadingSpaces ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : spacesData?.spaces.length === 0 ? (
                <EmptyState query={search.q} messageKey="search.noSpacesFound" />
              ) : (
                <div className="space-y-3">
                  {spacesData?.spaces.map((space) => (
                    <Link
                      key={space.spaceId}
                      to="/spaces/$spaceId"
                      params={{ spaceId: space.spaceId }}
                    >
                      <Card className="transition-shadow hover:shadow-md">
                        <CardContent className="flex items-center gap-4 p-4">
                          {space.iconUrl ? (
                            <img
                              src={space.iconUrl}
                              alt={space.name}
                              className="h-12 w-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                              <Users className="h-6 w-6 text-primary" aria-hidden="true" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-medium">{space.name}</p>
                            {space.description && (
                              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                                {space.description}
                              </p>
                            )}
                            <p className="mt-1 text-sm text-muted-foreground">
                              {space.subscriberCount} {t('search.members')}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                {t('common.back')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('search.pageOf', { page: page + 1, total: `${totalPages} (${total})` })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
              >
                {t('admin.bans.next')}
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          )}
        </>
      )}

      {!search.q && (
        <div className="py-12 text-center text-muted-foreground">
          <Search className="mx-auto h-12 w-12 opacity-50" aria-hidden="true" />
          <p className="mt-4">{t('search.emptyPrompt')}</p>
        </div>
      )}
    </div>
  );
}

function EmptyState({ query, messageKey }: { query: string; messageKey: string }) {
  const { t } = useTranslation('community');
  return (
    <div className="py-12 text-center text-muted-foreground">
      <Search className="mx-auto h-12 w-12 opacity-50" aria-hidden="true" />
      <p className="mt-4">
        {t(messageKey, { query })}
      </p>
    </div>
  );
}
